import {
  findFirstNode,
  findNodes,
  insertNodeAtChildIndex,
  removeNodeFromTree,
} from '../../../components/Tree/helpers/helpers';
import type { NodeBase, NodeBaseItem } from '../../../entities/Node';
import { getCyclicIndex } from '../../../utils/array/array';
import { destructId, getPathById } from '../../../utils/tree/treeNode';

export const DIRECTION_UP = '-' as const;
export const DIRECTION_DOWN = '+' as const;

type Direction = typeof DIRECTION_UP | typeof DIRECTION_DOWN;

interface MoveNodeParams {
  direction: Direction;
  node: NodeBase;
  treeData: NodeBase[];
}

interface NodeToMove {
  node: NodeBase;
  path: string[];
  parent: NodeBase;
}

interface MoveNodeResult {
  newParentId: string | undefined;
  oldParentId: string | undefined;
  treeData: NodeBase[];
  placedAfterId: string | undefined;
  oldPosition: number;
  newPosition: number;
}

interface DestructIdResult {
  categoryId?: string;
  catalogId?: string;
  value?: string;
}

interface FindNodesResult {
  matches: NodeBaseItem[];
}

interface TreeOperationResult {
  treeData: NodeBase[];
}

/**
 * Finds the parent node and all sibling parent nodes
 */
function findParentAndSiblingParents(
  treeData: NodeBase[],
  node: NodeBase,
): { parent: NodeBaseItem | null; siblingParents: NodeBaseItem[] } {
  const result = findNodes({
    treeData,
    searchMethod: (nodeItem: NodeBaseItem) => nodeItem.node.id === node.parentId,
    searchFocusOffset: 0,
    searchQuery: null,
  }) as unknown as FindNodesResult;

  const parent = result.matches?.[0] ?? null;

  if (!parent) {
    return { parent: null, siblingParents: [] };
  }

  const parentCategoryId = parent.node.category?.categoryId;

  if (!parentCategoryId) {
    return { parent, siblingParents: [parent] };
  }

  // Find all parents with the same category ID
  const siblingResult = findNodes({
    treeData,
    searchMethod: (nodeItem: NodeBaseItem) =>
      nodeItem.node?.categoryId === parentCategoryId,
    searchFocusOffset: 0,
    searchQuery: null,
  }) as unknown as FindNodesResult;

  return {
    parent,
    siblingParents: siblingResult.matches ?? [parent],
  };
}

/**
 * Collects all nodes that need to be moved (handles double-exposed categories).
 */
function collectNodesToMove(
  node: NodeBase,
  parent: NodeBaseItem,
  siblingParents: NodeBaseItem[],
): NodeToMove[] {
  // If there's only one parent, just move the single node
  if (siblingParents.length <= 1) {
    return [
      {
        node,
        path: getPathById(node.id),
        parent: parent.node,
      },
    ];
  }

  // Multiple parents (double-exposed category) - find matching children in each
  const nodeCategoryId = node.category?.categoryId;

  return siblingParents.reduce<NodeToMove[]>((nodes, siblingParent) => {
    const matchingChild = siblingParent.node.children?.find(
      (child: NodeBase) => child.category?.categoryId === nodeCategoryId,
    );

    if (matchingChild) {
      nodes.push({
        node: matchingChild,
        path: getPathById(matchingChild.id),
        parent: siblingParent.node,
      });
    }

    return nodes;
  }, []);
}

/**
 * Calculates the target index for a move operation.
 */
function calculateTargetIndex(
  currentIndex: number,
  direction: Direction,
  childrenCount: number,
): number {
  const offset = direction === DIRECTION_DOWN ? 1 : -1;
  return getCyclicIndex(
    { length: childrenCount } as unknown[],
    currentIndex + offset,
  );
}

/**
 * Applies move operations to the tree data.
 * Batches removals and insertions for better performance.
 */
function applyMoveOperations(
  nodesToMove: NodeToMove[],
  direction: Direction,
  initialTreeData: NodeBase[],
): { treeData: NodeBase[]; oldPosition: number } {
  let treeData = initialTreeData;
  let oldPosition = -1;

  for (let i = 0; i < nodesToMove.length; i++) {
    const { node: nodeToMove, path, parent: parentNode } = nodesToMove[i];

    const currentIndex =
      parentNode.children?.findIndex(
        (child: NodeBase) => child.id === nodeToMove.id,
      ) ?? -1;

    if (i === 0) {
      oldPosition = currentIndex;
    }

    const targetIndex = calculateTargetIndex(
      currentIndex,
      direction,
      parentNode.children?.length ?? 0,
    );

    // Remove the node
    treeData = (
      removeNodeFromTree({
        path,
        treeData,
      }) as TreeOperationResult
    ).treeData;

    // Insert at new position
    treeData = (
      insertNodeAtChildIndex({
        childIndex: targetIndex,
        newNode: nodeToMove,
        treeData,
      }) as TreeOperationResult
    ).treeData;
  }

  return { treeData, oldPosition };
}

/**
 * Extracts result metadata from the final tree state.
 */
function extractMoveResult(
  treeData: NodeBase[],
  node: NodeBase,
  oldPosition: number,
): Omit<MoveNodeResult, 'treeData'> {
  const newParentItem = findFirstNode({
    treeData,
    searchMethod: (nodeItem: NodeBaseItem) => nodeItem.node.id === node.parentId,
    ignoreCollapsed: false,
  }) as unknown as NodeBaseItem | null;

  const newParentNode = newParentItem?.node;

  if (!newParentNode) {
    return {
      newParentId: undefined,
      oldParentId: (destructId(node.parentId) as DestructIdResult).categoryId,
      placedAfterId: undefined,
      oldPosition,
      newPosition: -1,
    };
  }

  const newIndex =
    newParentNode.children?.findIndex((child: NodeBase) => child.id === node.id) ??
    -1;

  const placedAfterId =
    newIndex < 1 ? undefined : newParentNode.children?.[newIndex - 1]?.categoryId;

  return {
    newParentId: (destructId(newParentNode.id) as DestructIdResult).categoryId,
    oldParentId: (destructId(node.parentId) as DestructIdResult).categoryId,
    placedAfterId,
    oldPosition,
    newPosition: newIndex,
  };
}

/**
 * Moves a node up or down within its parent's children.
 * Handles double-exposed categories by moving all instances.
 *
 * @param direction - '+' to move down, '-' to move up
 * @param node - The node to move
 * @param treeData - The current tree data
 * @returns The updated tree data and position information
 */
export function moveNodeInTreeData({
  direction,
  node,
  treeData: initialTreeData,
}: MoveNodeParams): MoveNodeResult {
  // Find the parent node and any "sibling parents" - other nodes in the tree
  // that represent the same category (double-exposed categories).
  const { parent, siblingParents } = findParentAndSiblingParents(
    initialTreeData,
    node,
  );

  if (!parent) {
    throw new Error(`Parent node not found for node: ${node.id}`);
  }

  // Collect all nodes that need to be moved
  const nodesToMove = collectNodesToMove(node, parent, siblingParents);

  if (nodesToMove.length === 0) {
    throw new Error(`No nodes found to move for node: ${node.id}`);
  }

  // Apply the move operations
  const { treeData, oldPosition } = applyMoveOperations(
    nodesToMove,
    direction,
    initialTreeData,
  );

  // Extract result metadata
  const result = extractMoveResult(treeData, node, oldPosition);

  return {
    ...result,
    treeData,
  };
}
